import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NotificationService, NotificationConfiguration } from '../../services/notification.service';
import { ColDef, GridApi, GridReadyEvent, GridOptions } from 'ag-grid-community';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-notification-config',
  templateUrl: './notification-config.component.html',
  styleUrls: ['./notification-config.component.css']
})
export class NotificationConfigComponent implements OnInit {
  configurations: NotificationConfiguration[] = [];
  roles: any[] = [];
  templates: any[] = [];
  serviceStatuses: any[] = [];
  maintenanceStatuses: any[] = [];
  loading = false;
  showForm = false;
  editingId: number | null = null;
  private modal: any;

  configForm: FormGroup;

  // AG-Grid properties
  private gridApi!: GridApi;
  columnDefs: ColDef[] = [
    {
      headerName: 'Nombre',
      field: 'name',
      cellRenderer: (params: any) => {
        return `<code>${params.value}</code>`;
      }
    },
    {
      headerName: 'Título',
      field: 'title',
    },
    {
      headerName: 'Push',
      field: 'push_enabled',
      cellRenderer: (params: any) => {
        const enabled = params.value;
        const badgeClass = enabled ? 'bg-success' : 'bg-secondary';
        const text = enabled ? 'Habilitado' : 'Deshabilitado';
        return `<span class="badge ${badgeClass}">${text}</span>`;
      }
    },
    {
      headerName: 'WhatsApp',
      field: 'whatsapp_enabled',
      cellRenderer: (params: any) => {
        const enabled = params.value;
        const badgeClass = enabled ? 'bg-success' : 'bg-secondary';
        const text = enabled ? 'Habilitado' : 'Deshabilitado';
        return `<span class="badge ${badgeClass}">${text}</span>`;
      }
    },
    {
      headerName: 'Roles',
      field: 'target_roles',
      cellRenderer: (params: any) => {
        const rolesText = this.getTargetRolesText(params.value);
        return `<small>${rolesText}</small>`;
      }
    },
    {
      headerName: 'Grupo WhatsApp',
      field: 'whatsapp_group_number',
      cellRenderer: (params: any) => {
        const groupNumber = params.value;
        if (groupNumber && params.data.whatsapp_enabled) {
          return `<small class="text-success">${groupNumber}</small>`;
        }
        return '<small class="text-muted">No configurado</small>';
      }
    },
    {
      headerName: 'Estado',
      field: 'is_active',
      cellRenderer: (params: any) => {
        const active = params.value;
        const badgeClass = active ? 'bg-success' : 'bg-danger';
        const text = active ? 'Activo' : 'Inactivo';
        return `<span class="badge ${badgeClass}">${text}</span>`;
      }
    },
    {
      headerName: 'Acciones',
      field: 'actions',
      cellRenderer: (params: any) => {
        const config = params.data;
        const toggleIcon = config.is_active ? 'ti-toggle-right' : 'ti-toggle-left';
        const toggleTitle = config.is_active ? 'Desactivar' : 'Activar';
        
        return `
          <div class="btn-group btn-group-sm" role="group">
            <button class="btn btn-outline-primary btn-edit" title="Editar">
              <i class="ti ti-edit"></i>
            </button>
            <button class="btn btn-outline-warning btn-toggle" title="${toggleTitle}">
              <i class="ti ${toggleIcon}"></i>
            </button>
            <button class="btn btn-outline-danger btn-delete" title="Eliminar">
              <i class="ti ti-trash"></i>
            </button>
          </div>
        `;
      },
      onCellClicked: (params: any) => {
        const target = params.event.target;
        const config = params.data;
        
        if (target.closest('.btn-edit')) {
          this.editConfiguration(config);
        } else if (target.closest('.btn-toggle')) {
          this.toggleConfiguration(config);
        } else if (target.closest('.btn-delete')) {
          this.deleteConfiguration(config);
        }
      }
    }
  ];

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1
  };

  gridOptions: GridOptions = {
    pagination: false,
    paginationPageSize: 10,
    domLayout: 'autoHeight' as const
  };

  constructor(
    private fb: FormBuilder,
    private notificationService: NotificationService
  ) {
    this.configForm = this.fb.group({
      name: ['', [Validators.required]],
      title: ['', [Validators.required]],
      message_template: ['', [Validators.required]],
      push_enabled: [true],
      whatsapp_enabled: [false],
      whatsapp_group_number: [''],
      target_roles: [[]],
      conditions: [null],
      service_statuses: [[]],
      maintenance_statuses: [[]],
      is_active: [true]
    });
  }

  ngOnInit(): void {
    this.loadRoles();
    this.loadConfigurations();
    this.loadTemplates();
    this.loadServiceStatuses();
    this.loadMaintenanceStatuses();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
  }

  loadConfigurations(): void {
    this.loading = true;
    this.notificationService.getNotificationConfigurations().subscribe({
      next: (response) => {
        this.configurations = response.data || [];
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading configurations:', error);
        this.loading = false;
        Swal.fire('Error', 'No se pudieron cargar las configuraciones', 'error');
      }
    });
  }

  loadRoles(): void {
    this.notificationService.getNotificationRoles().subscribe({
      next: (response) => {
        this.roles = response.data || [];
      },
      error: (error) => {
        console.error('Error loading roles:', error);
      }
    });
  }

  loadTemplates(): void {
    this.notificationService.getNotificationTemplates().subscribe({
      next: (response) => {
        this.templates = response.data || [];
      },
      error: (error) => {
        console.error('Error loading templates:', error);
      }
    });
  }

  loadServiceStatuses(): void {
    this.notificationService.getServiceStatuses().subscribe({
      next: (response) => {
        this.serviceStatuses = response.data || [];
      },
      error: (error) => {
        console.error('Error loading service statuses:', error);
      }
    });
  }

  loadMaintenanceStatuses(): void {
    this.notificationService.getMaintenanceStatuses().subscribe({
      next: (response) => {
        this.maintenanceStatuses = response.data || [];
      },
      error: (error) => {
        console.error('Error loading maintenance statuses:', error);
      }
    });
  }

  showCreateForm(): void {
    this.showForm = true;
    this.editingId = null;
    this.configForm.reset({
      push_enabled: true,
      whatsapp_enabled: false,
      whatsapp_group_number: '',
      target_roles: [],
      service_statuses: [],
      maintenance_statuses: [],
      is_active: true
    });
    
    // Show modal
    setTimeout(() => {
      const modalElement = document.getElementById('configModal');
      if (modalElement) {
        this.modal = new bootstrap.Modal(modalElement);
        this.modal.show();
      }
    }, 100);
  }

  editConfiguration(config: NotificationConfiguration): void {
    this.showForm = true;
    this.editingId = config.id!;
    this.configForm.patchValue(config);
    
    // Show modal
    setTimeout(() => {
      const modalElement = document.getElementById('configModal');
      if (modalElement) {
        this.modal = new bootstrap.Modal(modalElement);
        this.modal.show();
      }
    }, 100);
  }

  useTemplate(template: any): void {
    this.configForm.patchValue({
      name: template.name,
      title: template.title,
      message_template: template.message_template,
      push_enabled: template.push_enabled,
      whatsapp_enabled: template.whatsapp_enabled
    });
  }

  onSubmit(): void {
    if (this.configForm.valid) {
      const formData = this.configForm.value;
      
      if (this.editingId) {
        this.updateConfiguration(this.editingId, formData);
      } else {
        this.createConfiguration(formData);
      }
    }
  }

  createConfiguration(data: NotificationConfiguration): void {
    this.notificationService.createNotificationConfiguration(data).subscribe({
      next: (response) => {
        Swal.fire('Éxito', 'Configuración creada correctamente', 'success');
        this.loadConfigurations();
        this.cancelForm();
      },
      error: (error) => {
        console.error('Error creating configuration:', error);
        Swal.fire('Error', 'No se pudo crear la configuración', 'error');
      }
    });
  }

  updateConfiguration(id: number, data: NotificationConfiguration): void {
    this.notificationService.updateNotificationConfiguration(id, data).subscribe({
      next: (response) => {
        Swal.fire('Éxito', 'Configuración actualizada correctamente', 'success');
        this.loadConfigurations();
        this.cancelForm();
      },
      error: (error) => {
        console.error('Error updating configuration:', error);
        Swal.fire('Error', 'No se pudo actualizar la configuración', 'error');
      }
    });
  }

  toggleConfiguration(config: NotificationConfiguration): void {
    this.notificationService.toggleNotificationConfiguration(config.id!).subscribe({
      next: (response) => {
        config.is_active = !config.is_active;
        const status = config.is_active ? 'activada' : 'desactivada';
        Swal.fire('Éxito', `Configuración ${status} correctamente`, 'success');
      },
      error: (error) => {
        console.error('Error toggling configuration:', error);
        Swal.fire('Error', 'No se pudo cambiar el estado de la configuración', 'error');
      }
    });
  }

  deleteConfiguration(config: NotificationConfiguration): void {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.notificationService.deleteNotificationConfiguration(config.id!).subscribe({
          next: (response) => {
            Swal.fire('Eliminado', 'Configuración eliminada correctamente', 'success');
            this.loadConfigurations();
          },
          error: (error) => {
            console.error('Error deleting configuration:', error);
            Swal.fire('Error', 'No se pudo eliminar la configuración', 'error');
          }
        });
      }
    });
  }

  cancelForm(): void {
    this.showForm = false;
    this.editingId = null;
    this.configForm.reset();
    
    // Hide modal
    if (this.modal) {
      this.modal.hide();
    }
  }

  getRoleName(roleId: number): string {
    const role = this.roles.find(r => r.id === roleId);
    return role ? role.name : 'Rol desconocido';
  }

  getTargetRolesText(targetRoles: number[]): string {
    if (!targetRoles || targetRoles.length === 0) {
      return 'Ninguno';
    }
    return targetRoles.map(roleId => this.getRoleName(roleId)).join(', ');
  }
}
